/**
 * Invoices > Auto-invoicing (tracker item 37).
 *
 * The invoices a run would create now (one row per group: a per-visit job,
 * or a consolidated customer's month), the completed jobs it would not
 * invoice and why, and the run history. The office selects groups (all by
 * default) and runs them: each becomes its own invoice, created as a draft
 * (or sent, when auto-send is on) through the normal invoice path. Shown in
 * every mode: in OFF mode it is the manual way to auto-invoice.
 *
 * Route-gated to ADMIN / OFFICE_MANAGER, like the backend
 * requireRole('ADMIN', 'OFFICE_MANAGER') on /api/auto-invoicing.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { createAPIClient, IAutoInvoicingApi } from '../api';
import * as types from '../api/types';
import { primaryButtonClass, secondaryButtonClass } from '../components/formStyles';
import { invoiceDate, invoiceMoney, invoiceStatus } from './invoiceUi';
import { COMPANY_SETTINGS_PATH, toggleSelection } from './financeChargeUi';
import {
  MODE_LABELS,
  TRIGGER_LABELS,
  autoInvoicingError,
  autoInvoicingModeDescription,
  defaultGroupSelection,
  groupMethodLabel,
  runConfirmMessage,
  selectedGroupsTotal,
  skipReasonLabel,
  toggleAllGroups,
} from './autoInvoicingUi';
import { Toast } from '../components/Toast';

const cell = (numeric = false) => `px-4 py-3 ${numeric ? 'text-right tabular-nums whitespace-nowrap' : 'text-left'}`;
const RUN_HISTORY_LIMIT = 10;

function RunResult({ result }: { result: types.AutoInvoiceRunResult }) {
  const created = result.results.filter((r) => r.status === 'CREATED');
  const other = result.results.filter((r) => r.status !== 'CREATED');
  return (
    <section aria-label="Run result" className="mb-6 rounded-lg bg-white p-4 shadow sm:p-5 break-words">
      <h2 className="text-lg font-semibold text-gray-900">Run result</h2>
      {created.length === 0 ? (
        <><Toast kind="info">No invoices were created.</Toast><p className="mt-2 text-gray-700">No invoices were created.</p></>
      ) : (
        <>
          <Toast kind="success">Created {created.length} {created.length === 1 ? 'invoice' : 'invoices'}, dated {invoiceDate(result.runDate)}.</Toast>
          <p className="mt-2 text-green-800">
            Created {created.length} {created.length === 1 ? 'invoice' : 'invoices'}, dated {invoiceDate(result.runDate)}.
          </p>
        </>
      )}
      {created.length > 0 && (
        <ul className="mt-3 space-y-1 text-sm">
          {created.map((entry) => (
            <li key={entry.key}>
              <Link to={`/invoices/${entry.invoiceId}`} className="font-semibold text-blue-700 hover:underline">#{entry.invoiceNumber}</Link>
              {' '}{entry.billingCustomerName}: {invoiceMoney(entry.total ?? '0')}
              {entry.invoiceStatus && <span className="text-gray-500"> ({invoiceStatus(entry.invoiceStatus)})</span>}
              {entry.sendError && <span className="text-red-700"> Not sent: {entry.sendError}</span>}
            </li>
          ))}
        </ul>
      )}
      {other.length > 0 && (
        <div className="mt-4">
          <h3 className="text-sm font-semibold text-gray-900">Not invoiced ({other.length})</h3>
          <ul className="mt-1 space-y-1 text-sm">
            {other.map((entry) => (
              <li key={entry.key} className={entry.status === 'FAILED' ? 'text-red-700' : 'text-gray-700'}>
                {entry.billingCustomerName || entry.key}: {entry.status === 'FAILED' ? 'Failed (retried next run)' : skipReasonLabel(entry.reason)}
                {entry.message && <span className="text-gray-500"> ({entry.message})</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

export function AutoInvoicingPage() {
  const api = useMemo<IAutoInvoicingApi>(() => createAPIClient(), []);
  const [preview, setPreview] = useState<types.AutoInvoicePreview | null>(null);
  const [runs, setRuns] = useState<types.AutoInvoiceRunSummary[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<types.AutoInvoiceRunResult | null>(null);
  const requestSeq = useRef(0);

  const load = useCallback(async (options: { keepResult?: boolean } = {}) => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    if (!options.keepResult) setResult(null);
    try {
      const [next, history] = await Promise.all([api.previewAutoInvoicing(), api.listAutoInvoicingRuns(RUN_HISTORY_LIMIT)]);
      if (seq !== requestSeq.current) return;
      setPreview(next);
      setRuns(history);
      setSelected(defaultGroupSelection(next.groups));
    } catch (cause) {
      if (seq !== requestSeq.current) return;
      setPreview(null);
      setSelected([]);
      setError(autoInvoicingError(cause, 'Could not load the auto-invoicing preview'));
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const groups = preview?.groups ?? [];
  const keys = groups.map((group) => group.key);
  const selectedKeys = selected.filter((key) => keys.includes(key));
  const total = selectedGroupsTotal(groups, selectedKeys);
  const allSelected = keys.length > 0 && selectedKeys.length === keys.length;
  const busy = loading || running;

  async function handleRun() {
    if (!preview || selectedKeys.length === 0 || running) return;
    if (!window.confirm(runConfirmMessage(selectedKeys.length, total, preview.autoSend))) return;
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      // Only the groups the user reviewed
      const outcome = await api.runAutoInvoicing({ groups: selectedKeys });
      setResult(outcome);
      await load({ keepResult: true });
    } catch (cause) {
      setError(autoInvoicingError(cause, 'Could not run auto-invoicing'));
    } finally {
      setRunning(false);
    }
  }

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <Link to="/invoices" className="inline-block py-2 text-sm text-blue-700 hover:underline sm:py-0">← Invoices</Link>
      <h1 className="text-2xl font-bold text-gray-900 mt-2">Auto-invoicing</h1>
      <p className="mt-1 mb-4 max-w-3xl text-sm text-gray-600">
        Completed jobs ready to bill, grouped the way auto-invoicing invoices them: one invoice per job for per-visit customers, one
        invoice per month for consolidated customers (after the month ends). Invoices are billed to the property's billing customer,
        dated today, with no discounts applied.
      </p>

      {preview && (
        <div role="note" className="mb-6 max-w-3xl rounded-md border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
          <p>
            <strong>Mode: {MODE_LABELS[preview.mode] ?? preview.mode}.</strong> {autoInvoicingModeDescription(preview.mode, preview.nightlyHour)}
          </p>
          <p className="mt-1">
            {preview.startDate ? <>Only jobs completed on or after {invoiceDate(preview.startDate)} are included. </> : null}
            {preview.autoSend ? 'Auto-send is on: new invoices are sent right away.' : 'New invoices are created as drafts to review and send.'}
          </p>
          <Link to={COMPANY_SETTINGS_PATH} className="mt-1 inline-block py-2 font-semibold underline sm:py-0">Company settings</Link>
        </div>
      )}

      {error && <Toast kind="error">{error}</Toast>}
      {result && <RunResult result={result} />}

      {loading && !preview && <p className="text-gray-600">Loading preview…</p>}

      {preview && (
        <>
          <section aria-label="Invoices to create" className="rounded-lg bg-white p-4 shadow sm:p-5">
            <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="text-lg font-semibold text-gray-900">Ready to invoice as of {invoiceDate(preview.asOfDate)}</h2>
              <button type="button" className={`${secondaryButtonClass} min-h-[44px] sm:min-h-0`} disabled={busy} onClick={() => void load()}>
                {loading ? 'Loading…' : 'Refresh'}
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="responsive-table min-w-full divide-y divide-gray-200 text-sm">
                <caption className="sr-only">Invoices auto-invoicing would create</caption>
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-4 py-3 w-10">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        disabled={busy || keys.length === 0}
                        onChange={() => setSelected(toggleAllGroups(groups, selectedKeys))}
                        aria-label="Select all invoices"
                        className="h-4 w-4 rounded border-gray-300"
                      />
                    </th>
                    {[
                      { label: 'Bill to' },
                      { label: 'Billing' },
                      { label: 'Jobs' },
                      { label: 'Subtotal', numeric: true },
                      { label: 'Tax', numeric: true },
                      { label: 'Total', numeric: true },
                    ].map((column) => (
                      <th key={column.label} scope="col" className={`px-4 py-3 font-medium text-gray-600 ${column.numeric ? 'text-right' : 'text-left'}`}>
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {groups.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-10 text-center text-gray-600">No completed jobs are ready to invoice.</td>
                    </tr>
                  ) : groups.map((group) => (
                    <tr key={group.key}>
                      <td className="px-4 py-3 w-10">
                        <input
                          type="checkbox"
                          checked={selectedKeys.includes(group.key)}
                          disabled={busy}
                          onChange={() => setSelected((current) => toggleSelection(current, group.key))}
                          aria-label={`Invoice ${group.billingCustomerName} (${groupMethodLabel(group)})`}
                          className="h-5 w-5 rounded border-gray-300 sm:h-4 sm:w-4"
                        />
                      </td>
                      <td className={`${cell()} min-w-0 break-words`} data-label="Bill to" data-primary>
                        <Link to={`/customers/${group.billingCustomerId}`} className="font-semibold text-blue-700 hover:underline">{group.billingCustomerName}</Link>
                        {group.customerId !== group.billingCustomerId && <span className="block text-xs text-gray-500">for {group.customerName}</span>}
                      </td>
                      <td className={`${cell()} whitespace-nowrap`} data-label="Billing">{groupMethodLabel(group)}</td>
                      <td className={`${cell()} min-w-0 break-words`} data-label="Jobs">
                        <ul className="space-y-0.5 text-right md:text-left">
                          {group.jobs.map((job) => (
                            <li key={job.jobId}>
                              <Link to={`/jobs/${job.jobId}`} className="text-blue-700 hover:underline">{job.propertyAddress || job.jobId}</Link>
                              {job.completedDate && <span className="text-gray-500"> · {invoiceDate(job.completedDate)}</span>}
                            </li>
                          ))}
                        </ul>
                      </td>
                      <td className={cell(true)} data-label="Subtotal">{invoiceMoney(group.subtotal)}</td>
                      <td className={cell(true)} data-label="Tax">{invoiceMoney(group.tax)}</td>
                      <td className={`${cell(true)} font-semibold`} data-label="Total">{invoiceMoney(group.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-5 flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-gray-700" aria-live="polite">
                {keys.length === 0
                  ? 'Nothing to invoice.'
                  : <>Selected <strong>{selectedKeys.length}</strong> of {keys.length} · Total <strong>{invoiceMoney(total)}</strong></>}
              </p>
              <button type="button" disabled={busy || selectedKeys.length === 0} onClick={() => void handleRun()} className={`${primaryButtonClass} min-h-[44px] w-full sm:min-h-0 sm:w-auto`}>
                {running ? 'Invoicing…' : 'Invoice selected'}
              </button>
            </div>
          </section>

          <section aria-label="Skipped jobs" className="mt-6 rounded-lg bg-white p-4 shadow sm:p-5">
            <h2 className="text-lg font-semibold text-gray-900">Not invoiced ({preview.skipped.length})</h2>
            <p className="mt-1 text-sm text-gray-600">Completed jobs without an invoice that auto-invoicing leaves alone, and why.</p>
            {preview.skipped.length === 0 ? (
              <p className="mt-3 text-sm text-gray-600">None.</p>
            ) : (
              <ul className="mt-3 divide-y divide-gray-100 text-sm">
                {preview.skipped.map((job) => (
                  <li key={job.jobId} className="py-2 flex flex-wrap justify-between gap-2">
                    <span className="min-w-0 break-words">
                      <Link to={`/jobs/${job.jobId}`} className="text-blue-700 hover:underline">{job.propertyAddress || job.jobId}</Link>
                      <span className="text-gray-500"> · {job.billingCustomerName}</span>
                    </span>
                    <span className="font-medium text-gray-700" title={job.message}>{skipReasonLabel(job.reason)}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section aria-label="Run history" className="mt-6 rounded-lg bg-white p-4 shadow sm:p-5">
            <h2 className="text-lg font-semibold text-gray-900">Recent runs</h2>
            {runs.length === 0 ? (
              <p className="mt-3 text-sm text-gray-600">No runs yet.</p>
            ) : (
              <table className="responsive-table mt-3 min-w-full divide-y divide-gray-200 text-sm">
                <caption className="sr-only">Recent auto-invoicing runs</caption>
                <thead className="bg-gray-50">
                  <tr>
                    {['When', 'Trigger', 'Created', 'Skipped', 'Failed', 'Notes'].map((label) => (
                      <th key={label} scope="col" className="px-4 py-2 text-left font-medium text-gray-600">{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {runs.map((run) => (
                    <tr key={run.id}>
                      <td className="px-4 py-2 whitespace-nowrap" data-label="When" data-primary>{new Date(run.startedAt).toLocaleString()}</td>
                      <td className="px-4 py-2" data-label="Trigger">{TRIGGER_LABELS[run.trigger] ?? run.trigger}</td>
                      <td className="px-4 py-2 tabular-nums" data-label="Created">{run.createdCount}</td>
                      <td className="px-4 py-2 tabular-nums" data-label="Skipped">{run.skippedCount}</td>
                      <td className={`px-4 py-2 tabular-nums ${run.failedCount > 0 ? 'text-red-700 font-semibold' : ''}`} data-label="Failed">{run.failedCount}</td>
                      <td className="px-4 py-2 text-gray-600 min-w-0 break-words" data-label="Notes">
                        {run.error ?? run.items
                          .filter((item) => item.status === 'FAILED')
                          .map((item) => item.message)
                          .filter((m, i, all) => m && all.indexOf(m) === i)
                          .join('; ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </main>
  );
}
